import type { GridFilterModel } from '@mui/x-data-grid';
import { normalizeText } from './textNormalization';

type ProvinceOption = { value: string; label: string };

/**
 * Mã sentinel dùng khi người dùng đã đặt bộ lọc tỉnh nhưng không khớp tỉnh nào.
 * Không phải mã tỉnh thật nên gửi lên backend sẽ trả về rỗng (fail-closed),
 * thay vì bỏ qua bộ lọc và hiện toàn bộ dữ liệu (fail-open).
 */
export const NO_MATCH_PROVINCE_CODE = '__no-match__';

const VALUE_LESS_OPERATORS = new Set(['isEmpty', 'isNotEmpty']);

/**
 * Nhãn option có dạng "<mã> - <tên>" (xem GeneralStatService). Bỏ phần mã để
 * so khớp đúng với tên tỉnh mà người dùng nhìn thấy và gõ vào ô lọc.
 */
function stripProvinceCodePrefix(option: ProvinceOption): string {
	const prefix = `${option.value} - `;
	return option.label.startsWith(prefix) ? option.label.slice(prefix.length) : option.label;
}

/**
 * Matches a province label against a filter value using the specified operator.
 * Supports: equals, startsWith, endsWith, isEmpty, isNotEmpty, contains (default).
 */
export function matchProvinceByOperator(
	provinceLabel: string,
	filterValue: string,
	operator?: string
): boolean {
	const normalizedLabel = provinceLabel.toLowerCase();
	const normalizedValue = filterValue.toLowerCase();

	switch (operator) {
		case 'equals':
			return normalizedLabel === normalizedValue;
		case 'startsWith':
			return normalizedLabel.startsWith(normalizedValue);
		case 'endsWith':
			return normalizedLabel.endsWith(normalizedValue);
		case 'isEmpty':
			return !provinceLabel;
		case 'isNotEmpty':
			return !!provinceLabel;
		case 'contains':
		default:
			return normalizedLabel.includes(normalizedValue);
	}
}

interface ExtractedFilters {
	maTinhMoi?: string[];
	maTinh?: string[];
	hasCodeNameChange?: boolean;
	top149?: boolean;
}

/**
 * Extracts province filter values and boolean flags from a DataGrid GridFilterModel.
 * Province name filters are resolved to codes via the provided option arrays.
 *
 * Fail-closed: nếu người dùng đã đặt bộ lọc cho một cột tỉnh nhưng không có tỉnh
 * nào khớp, trả về mã sentinel (không khớp dữ liệu thật) thay vì `undefined` —
 * tránh việc lưới âm thầm hiện lại toàn bộ dữ liệu như chưa lọc.
 */
export function extractFiltersFromModel(
	filterModel: GridFilterModel,
	mergedProvinces: ProvinceOption[],
	provinces: ProvinceOption[]
): ExtractedFilters {
	const maTinhMoi: string[] = [];
	const maTinh: string[] = [];
	let maTinhMoiFilterActive = false;
	let maTinhFilterActive = false;
	let hasCodeNameChange: boolean | undefined;
	let top149: boolean | undefined;

	for (const item of filterModel.items) {
		const hasValue = item.value !== undefined && item.value !== '';
		const isValueLessOperator = !!item.operator && VALUE_LESS_OPERATORS.has(item.operator);
		const shouldApply = hasValue || isValueLessOperator;
		const rawValue = hasValue ? String(item.value) : '';

		if (item.field === 'tenTinhSauSapNhap' && shouldApply) {
			maTinhMoiFilterActive = true;
			mergedProvinces
				.filter(p => matchProvinceByOperator(stripProvinceCodePrefix(p), rawValue, item.operator))
				.forEach(p => maTinhMoi.push(p.value));
		}
		if (item.field === 'tenTinh' && shouldApply) {
			maTinhFilterActive = true;
			provinces
				.filter(p => matchProvinceByOperator(stripProvinceCodePrefix(p), rawValue, item.operator))
				.forEach(p => maTinh.push(p.value));
		}
		if (item.field === 'hasCodeNameChange' && item.value !== undefined && item.value !== '') {
			if (item.operator === 'is' || !item.operator) {
				hasCodeNameChange = typeof item.value === 'boolean' ? item.value : item.value === 'true';
			}
		}
		if (item.field === 'top149' && item.value !== undefined && item.value !== '') {
			if (item.operator === 'is' || !item.operator) {
				top149 = typeof item.value === 'boolean' ? item.value : item.value === 'true';
			}
		}
	}

	return {
		maTinhMoi: maTinhMoiFilterActive ? (maTinhMoi.length > 0 ? maTinhMoi : [NO_MATCH_PROVINCE_CODE]) : undefined,
		maTinh: maTinhFilterActive ? (maTinh.length > 0 ? maTinh : [NO_MATCH_PROVINCE_CODE]) : undefined,
		hasCodeNameChange,
		top149
	};
}

export interface ProvinceSearchMatches {
	maTinh: string[];
	maTinhMoi: string[];
}

/**
 * Tìm mã tỉnh (gốc và sau sáp nhập) khớp với chuỗi tìm kiếm tự do của ô tìm
 * kiếm chung. Cột "Sau sáp nhập" hiển thị tên từ danh mục mới, nên phải dò cả
 * hai danh mục — chỉ dò danh mục gốc sẽ bỏ sót tên tỉnh đang hiển thị trên màn
 * hình sau sáp nhập.
 */
export function resolveProvinceSearchMatches(
	searchValue: string,
	provinces: ProvinceOption[],
	mergedProvinces: ProvinceOption[]
): ProvinceSearchMatches {
	const normalized = normalizeText(searchValue);
	if (!normalized) return { maTinh: [], maTinhMoi: [] };

	return {
		maTinh: provinces
			.filter((p) => normalizeText(p.label).includes(normalized))
			.map((p) => p.value),
		maTinhMoi: mergedProvinces
			.filter((p) => normalizeText(p.label).includes(normalized))
			.map((p) => p.value),
	};
}
